import type { CreateUploadBody, CreateUploadResponse } from '@photos/shared';

const isYear = (t: string) => /^(19|20)\d\d$/.test(t);

export type UploadStatus = 'reading' | 'ready' | 'dup' | 'uploading' | 'done' | 'error';

export interface UploadItem {
  clientId: string;
  file: File;
  name: string;
  bytes: number;
  contentType: string;
  /** object URL for formats the browser can render, else null */
  previewUrl: string | null;
  /** EXIF DateTimeOriginal as wall-clock ISO ("…Z" but not converted from local time) */
  takenAt?: string;
  /** year used for auto tags (EXIF year, else last-modified year); set once the file was read */
  year?: string;
  sha256?: string;
  status: UploadStatus;
  /** 0..1 */
  progress: number;
  /** why a file was skipped or failed */
  note?: string;
  photoId?: string;
  target?: { url: string; headers: Record<string, string> };
}

export const SUPPORTED_RE = /\.(jpe?g|png|webp|gif|heic|heif|tiff?|cr3|cr2|nef|arw|dng|zip)$/i;
export const RENDERABLE_RE = /\.(jpe?g|png|webp|gif)$/i;

const TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  heic: 'image/heic',
  heif: 'image/heif',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  cr3: 'image/x-canon-cr3',
  cr2: 'image/x-canon-cr2',
  nef: 'image/x-nikon-nef',
  arw: 'image/x-sony-arw',
  dng: 'image/x-adobe-dng',
  zip: 'application/zip',
};

export function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i === -1 ? '' : name.slice(i + 1).toLowerCase();
}

export function contentTypeFor(name: string, browserType = ''): string {
  return browserType || TYPES[extOf(name)] || 'application/octet-stream';
}

export function isZip(name: string): boolean {
  return extOf(name) === 'zip';
}

/** Skip OS junk inside folders and ZIPs. */
export function isJunkPath(path: string): boolean {
  return path.split('/').some((seg) => seg.startsWith('.') || seg === '__MACOSX' || seg === 'Thumbs.db');
}

/** Year tag from a wall-clock ISO capture date ("2025-12-31T23:30:00Z" → "2025"); read from the string, no time zone math. */
export function yearOf(iso: string): string {
  return iso.slice(0, 4);
}

/** Year for auto tags: the EXIF capture year, else the file's last-modified year. */
export function itemYear(takenAt: string | undefined, lastModified: number): string {
  return takenAt ? yearOf(takenAt) : String(new Date(lastModified).getFullYear());
}

export interface AutoTagState {
  tags: string[];
  /**
   * Tags that were added automatically (shown with an "auto" mark, removable): capture years, and the uploader's
   * region tag. A removed auto tag stays in this list so it isn't added back.
   */
  auto: string[];
}

/** Starting tags for an upload: the current year (replaced by capture years once files are read) and the uploader's region. */
export function initialAutoTags(year: string, regionTag?: string): AutoTagState {
  const tags = regionTag ? [year, regionTag] : [year];
  return { tags, auto: [...tags] };
}

/**
 * The prototype's `applyAutoYears`: auto year tags follow the capture years of the files in the queue.
 * Auto tags whose year is no longer present are dropped, new years are prepended. When no file has a capture date,
 * the current year is used. Tags the user removed by hand stay removed unless their year is new.
 */
export function planAutoTags(state: AutoTagState, captureYears: Iterable<string>, fallbackYear: string): AutoTagState {
  const years = new Set(captureYears);
  if (!years.size) years.add(fallbackYear);
  let tags = [...state.tags];
  const auto = new Set(state.auto);
  for (const t of [...auto]) {
    if (isYear(t) && !years.has(t)) {
      tags = tags.filter((x) => x !== t);
      auto.delete(t);
    }
  }
  const sorted = [...years].sort();
  for (const y of sorted) {
    if (auto.has(y)) continue;
    auto.add(y);
    if (!tags.includes(y)) tags.unshift(y);
  }
  return { tags, auto: [...auto] };
}

export function captureYears(items: readonly UploadItem[]): string[] {
  return [...new Set(items.filter((i) => i.status !== 'dup' && i.year).map((i) => i.year!))].sort().reverse();
}

/** Files with the same fingerprint as an earlier file in the same drop are duplicates. */
export function markLocalDuplicates(items: readonly UploadItem[]): UploadItem[] {
  const seen = new Set<string>();
  return items.map((it) => {
    if (!it.sha256 || it.status === 'dup') return it;
    if (seen.has(it.sha256)) return { ...it, status: 'dup', note: 'Duplicate in this upload' };
    seen.add(it.sha256);
    return it;
  });
}

/** Where the photos go: an existing album, or a new one created with the upload. */
export type AlbumChoice = { albumId: string } | { newAlbumName: string };

/**
 * Body for POST /uploads from the files that are ready (hashed and not duplicates). Auto year chips the user kept go
 * in `autoYearTags` (each photo then gets only its own capture year); everything else (including a kept auto region
 * tag) applies to every photo.
 */
export function uploadBody(
  items: readonly UploadItem[],
  tagState: AutoTagState,
  visibility: CreateUploadBody['visibility'],
  album: AlbumChoice,
): CreateUploadBody {
  const autoYear = (t: string) => isYear(t) && tagState.auto.includes(t);
  return {
    ...album,
    tags: tagState.tags.filter((t) => !autoYear(t)),
    autoYearTags: tagState.tags.filter(autoYear),
    visibility,
    files: items
      .filter((i) => i.status === 'ready' && i.sha256)
      .map((i) => ({
        clientId: i.clientId,
        name: i.name.slice(0, 255),
        bytes: i.bytes,
        contentType: i.contentType,
        sha256: i.sha256!,
        ...(i.takenAt ? { takenAt: i.takenAt } : {}),
      })),
  };
}

/** Apply the POST /uploads response: mark duplicates already in Glean, attach upload targets. */
export function applyUploadResponse(items: readonly UploadItem[], res: CreateUploadResponse): UploadItem[] {
  const dups = new Set(res.duplicates);
  const targets = new Map(res.uploads.map((u) => [u.clientId, u]));
  return items.map((it) => {
    if (dups.has(it.clientId)) return { ...it, status: 'dup', note: 'Already in Glean' };
    const t = targets.get(it.clientId);
    if (!t) return it;
    return { ...it, photoId: t.photoId, target: { url: t.url, headers: t.headers } };
  });
}

export interface QueueStats {
  total: number;
  todo: number;
  dups: number;
  bytes: number;
  done: number;
  failed: number;
}

export function queueStats(items: readonly UploadItem[]): QueueStats {
  const live = items.filter((i) => i.status !== 'dup');
  return {
    total: items.length,
    todo: live.length,
    dups: items.length - live.length,
    bytes: live.reduce((a, i) => a + i.bytes, 0),
    done: items.filter((i) => i.status === 'done').length,
    failed: items.filter((i) => i.status === 'error').length,
  };
}
